import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import { fetchHostelHistory } from "../services/api";
import Footer from "./Footer";

function formatDate(dateString) {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (isNaN(date)) return "Invalid date";
  return date.toLocaleDateString("en-GB"); // dd/mm/yyyy
}

function groupBySession(history) {
  const grouped = {};
  history.forEach((entry) => {
    const session = entry.session_name || "Unknown Session";
    if (!grouped[session]) {
      grouped[session] = [];
    }
    grouped[session].push(entry);
  });
  return grouped;
}

export default function HostelHistory({ onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadHistory() {
      try {
        const res = await fetchHostelHistory();
        if (res.success) {
          setHistory(res.history);
        } else {
          setError(res.error || "Unable to fetch history.");
        }
      } catch (err) {
        setError("Error fetching allocation history.");
      } finally {
        setLoading(false);
      }
    }

    loadHistory();
  }, []);

  const groupedHistory = groupBySession(history);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        onLogout={onLogout}
      />

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex-1 flex flex-col md:ml-64">
        <Topbar
          setSidebarOpen={setSidebarOpen}
          title="Hostel Allocation History"
        />

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
          <h2 className="text-xl sm:text-2xl font-semibold text-blue-700 mb-4">
            Hostel Allocation History
          </h2>

          {loading && <p className="text-gray-500">Loading...</p>}
          {error && <p className="text-red-600">{error}</p>}

          {!loading && Object.keys(groupedHistory).length === 0 && (
            <p className="text-gray-500">No hostel allocation history found</p>
          )}

          {!loading &&
            Object.entries(groupedHistory).map(([sessionName, records], idx) => (
              <div
                key={idx}
                className="bg-white shadow rounded-xl overflow-hidden border border-gray-200"
              >
                <div className="bg-blue-50 px-4 sm:px-6 py-3 border-b border-blue-100">
                  <h3 className="text-base sm:text-lg font-semibold text-blue-800">
                    Academic Session: {sessionName}
                  </h3>
                </div>

                <div className="overflow-x-auto w-full">
                  <table className="min-w-full text-sm text-left block sm:table">
                    <thead className="bg-gray-100 text-gray-700 block sm:table-header-group">
                      <tr className="block sm:table-row">
                        <th className="px-4 py-2 sm:px-6 sm:py-3 block sm:table-cell font-medium">
                          Hostel Name
                        </th>
                        <th className="px-4 py-2 sm:px-6 sm:py-3 block sm:table-cell font-medium">
                          Room No.
                        </th>
                        <th className="px-4 py-2 sm:px-6 sm:py-3 block sm:table-cell font-medium">
                          Status
                        </th>
                        <th className="px-4 py-2 sm:px-6 sm:py-3 block sm:table-cell font-medium">
                          Change Date
                        </th>
                        <th className="px-4 py-2 sm:px-6 sm:py-3 block sm:table-cell font-medium">
                          No-Dues Date
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-800 block sm:table-row-group">
                      {records.map((entry, i) => (
                        <tr
                          key={i}
                          className="border-t block sm:table-row hover:bg-gray-50 transition"
                        >
                          <td className="px-4 py-2 sm:px-6 sm:py-3 block sm:table-cell">
                            {entry.hostel_name}
                          </td>
                          <td className="px-4 py-2 sm:px-6 sm:py-3 block sm:table-cell">
                            {entry.room_number}
                          </td>
                          <td className="px-4 py-2 sm:px-6 sm:py-3 block sm:table-cell capitalize text-blue-600">
                            {entry.status}
                          </td>
                          <td className="px-4 py-2 sm:px-6 sm:py-3 block sm:table-cell">
                            {formatDate(entry.change_date)}
                          </td>
                          <td className="px-4 py-2 sm:px-6 sm:py-3 block sm:table-cell">
                            {formatDate(entry.no_dues_date)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
        </main>
        {/* Footer */}
      <Footer />
      </div>
    </div>
  );
}
